/**
 * The email preference page's decisions (23 October 2026), kept apart from
 * React so a test can pin them.
 *
 * Mail scanners (link checkers, security gateways) open links in a headless
 * browser and run the page's JavaScript. So the page never changes anything
 * as it opens: the first request is always a read, and the footer's
 * Unsubscribe link (action=unsubscribe) only puts one prominent button in
 * front of the reader. Their tap sends the unsubscribe, so one tap from the
 * email still unsubscribes. A mail provider's own one-click button (RFC 8058
 * List-Unsubscribe-Post) is a POST to the Api and does not pass through this
 * page.
 */

export interface PreferenceLink {
  /** The signed token from the URL fragment (never a query string). */
  token: string;
  /** The footer's Unsubscribe link: show the one-tap unsubscribe first. */
  unsubscribeRequested: boolean;
}

export function readPreferenceLink(hash: string): PreferenceLink {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return { token: params.get('t') ?? '', unsubscribeRequested: params.get('action') === 'unsubscribe' };
}

/**
 * What identifies the link the page is showing (23 October 2026, review).
 * Opening a second link in the same tab (pasted into the address bar, or an
 * installed app catching it in its open window) changes only the fragment, a
 * same-document navigation that remounts nothing. The page remounts whenever
 * this changes, so it never shows, or writes for, the previous recipient.
 */
export function preferenceLinkKey(hash: string): string {
  const link = readPreferenceLink(hash);
  return `${link.token}|${link.unsubscribeRequested ? 'unsubscribe' : 'view'}`;
}

/** A plausible email address for the "email me a new link" form. */
export function looksLikeEmail(value: string): boolean {
  const v = value.trim();
  return v.length >= 3 && v.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** The only request the page makes on its own as it opens. */
export const OPENING_REQUEST = 'lookup' as const;

/**
 * What the page shows once the read answers: the one-tap unsubscribe when the
 * reader came from the Unsubscribe link and is still subscribed, else the
 * preferences (which say so when they are already unsubscribed).
 */
export function stageAfterLookup(
  state: { unsubscribed: boolean },
  unsubscribeRequested: boolean,
): 'confirm_unsubscribe' | 'preferences' {
  return unsubscribeRequested && !state.unsubscribed ? 'confirm_unsubscribe' : 'preferences';
}

/** The address bar after the reader's choice: the action dropped, so a reload
 *  or a shared screenshot does not offer it again. */
export function linkWithoutAction(pathname: string, token: string): string {
  return `${pathname}#t=${token}`;
}

/**
 * The attributes the marketing switches and Unsubscribe from all take
 * (23 October 2026, second review). The native `disabled` attribute moves
 * focus to the page body the moment it lands on the focused control, so it
 * is set only while there is nothing to show yet (loading). During a save the
 * controls are aria-disabled and ignore presses (inert), and focus stays.
 */
export function marketingControlState(opts: { loading: boolean; saving: boolean }): {
  disabled: boolean;
  ariaDisabled: true | undefined;
  inert: boolean;
} {
  return {
    disabled: opts.loading,
    ariaDisabled: opts.saving && !opts.loading ? true : undefined,
    inert: opts.loading || opts.saving,
  };
}
